import React, { useState } from "react";
import axios from "axios";
import { User, Lock, Check, AlertCircle, Key, Copy, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD = {
  background: "rgba(255, 255, 255, 0.18)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(3px)",
  borderRadius: "0.875rem",
  boxShadow: "0 4px 24px rgba(26,77,46,0.06)",
};

const inputStyle = {
  width: "100%",
  background: "rgba(255, 255, 255, 0.55)",
  border: "1px solid rgba(26,77,46,0.2)",
  borderRadius: "0.5rem",
  padding: "0.6rem 0.875rem",
  color: "#0F172A",
  fontSize: "0.9rem",
  fontFamily: "'Manrope', sans-serif",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block",
  fontFamily: "'Manrope', sans-serif",
  color: "#374151",
  fontSize: "0.8rem",
  fontWeight: 600,
  marginBottom: "0.4rem",
};

function StatusMsg({ msg }) {
  if (!msg) return null;
  const isSuccess = msg.type === "success";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "0.4rem",
      marginBottom: "0.75rem",
      color: isSuccess ? "#3d6b1f" : "#ef4444",
      fontSize: "0.8rem", fontFamily: "'Manrope', sans-serif",
      background: isSuccess ? "rgba(61,107,31,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${isSuccess ? "rgba(61,107,31,0.2)" : "rgba(239,68,68,0.2)"}`,
      borderRadius: "0.5rem", padding: "0.5rem 0.75rem",
    }}>
      {isSuccess ? <Check size={14} /> : <AlertCircle size={14} />}
      {msg.text}
    </div>
  );
}

function SubmitBtn({ loading, disabled, label, loadingLabel }) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      style={{
        padding: "0.6rem 1.4rem",
        borderRadius: "0.5rem",
        border: "none",
        background: disabled || loading ? "rgba(61,107,31,0.4)" : "#3d6b1f",
        color: "#fff",
        fontSize: "0.875rem",
        fontWeight: 600,
        fontFamily: "'Manrope', sans-serif",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        transition: "background 0.2s",
      }}
      onMouseEnter={(e) => { if (!disabled && !loading) e.currentTarget.style.background = "#4a7f25"; }}
      onMouseLeave={(e) => { if (!disabled && !loading) e.currentTarget.style.background = "#3d6b1f"; }}
    >
      {loading ? loadingLabel : label}
    </button>
  );
}

export default function SettingsPage({ user, token, onProfileUpdated }) {
  const [name, setName] = useState(user?.name || "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const [mcpToken, setMcpToken] = useState("");
  const [mcpLoading, setMcpLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleGenerateMcpToken = async () => {
    setMcpLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/mcp-token`, { headers: authHeaders });
      setMcpToken(data.token);
    } catch (err) {
      console.error("Failed to generate MCP token:", err);
    } finally {
      setMcpLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setNameLoading(true);
    setNameMsg(null);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/user/profile`,
        { name: name.trim() },
        { headers: authHeaders }
      );
      setNameMsg({ type: "success", text: "Display name updated successfully." });
      onProfileUpdated?.({ name: name.trim() });
    } catch (err) {
      setNameMsg({ type: "error", text: err.response?.data?.detail || "Failed to update name." });
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: "error", text: "New passwords do not match." });
      return;
    }
    if (newPwd.length < 6) {
      setPwdMsg({ type: "error", text: "Password must be at least 6 characters." });
      return;
    }
    setPwdLoading(true);
    setPwdMsg(null);
    try {
      await axios.post(
        `${BACKEND_URL}/api/user/change-password`,
        { current_password: currentPwd, new_password: newPwd },
        { headers: authHeaders }
      );
      setPwdMsg({ type: "success", text: "Password changed successfully." });
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      setPwdMsg({ type: "error", text: err.response?.data?.detail || "Failed to change password." });
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div
      data-testid="settings-page"
      style={{ width: "100%", maxWidth: "640px", display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      {/* ── Profile Card ── */}
      <div style={CARD}>
        <div style={{
          padding: "0.875rem 1.25rem",
          borderBottom: "1px solid rgba(26,77,46,0.12)",
          display: "flex", alignItems: "center", gap: "0.5rem",
        }}>
          <User size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 600 }}>
            Profile
          </span>
        </div>

        <form onSubmit={handleSaveName} style={{ padding: "1.25rem" }}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Display Name</label>
            <input
              data-testid="settings-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your display name"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.2)")}
            />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={labelStyle}>Email Address</label>
            <input
              data-testid="settings-email-input"
              value={user?.email || ""}
              disabled
              style={{ ...inputStyle, background: "rgba(0,0,0,0.04)", color: "#9ca3af", cursor: "not-allowed" }}
            />
            <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.72rem", margin: "0.25rem 0 0" }}>
              Email address cannot be changed.
            </p>
          </div>

          <StatusMsg msg={nameMsg} />

          <SubmitBtn
            loading={nameLoading}
            disabled={!name.trim() || name.trim() === user?.name}
            label="Save Changes"
            loadingLabel="Saving…"
          />
        </form>
      </div>

      {/* ── Change Password Card ── */}
      <div style={CARD}>
        <div style={{
          padding: "0.875rem 1.25rem",
          borderBottom: "1px solid rgba(26,77,46,0.12)",
          display: "flex", alignItems: "center", gap: "0.5rem",
        }}>
          <Lock size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 600 }}>
            Change Password
          </span>
        </div>

        <form onSubmit={handleChangePassword} style={{ padding: "1.25rem" }}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Current Password</label>
            <input
              data-testid="settings-current-pwd-input"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              placeholder="Enter current password"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.2)")}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>New Password</label>
            <input
              data-testid="settings-new-pwd-input"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Min. 6 characters"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.2)")}
            />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={labelStyle}>Confirm New Password</label>
            <input
              data-testid="settings-confirm-pwd-input"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Re-enter new password"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.2)")}
            />
          </div>

          <StatusMsg msg={pwdMsg} />

          <SubmitBtn
            loading={pwdLoading}
            disabled={!currentPwd || !newPwd || !confirmPwd}
            label="Change Password"
            loadingLabel="Changing…"
          />
        </form>
      </div>

      {/* ── MCP Token Card ── */}
      <div style={CARD}>
        <div style={{
          padding: "0.875rem 1.25rem",
          borderBottom: "1px solid rgba(26,77,46,0.12)",
          display: "flex", alignItems: "center", gap: "0.5rem",
        }}>
          <Key size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 600 }}>
            MCP API Token
          </span>
        </div>

        <div style={{ padding: "1.25rem" }}>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.85rem", lineHeight: 1.55, margin: "0 0 1rem" }}>
            Generate a token to connect Claude Desktop, Cursor, or any MCP-compatible AI client
            directly to your Serenity account. Pass it as <code style={{ background: "rgba(0,0,0,0.06)", padding: "0 4px", borderRadius: 3, fontFamily: "monospace" }}>Authorization: Bearer &lt;token&gt;</code> when
            connecting. Valid for 10 years.
          </p>

          {/* Token row */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Your MCP Token</label>
            {mcpToken ? (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  data-testid="mcp-token-display"
                  readOnly
                  value={mcpToken}
                  style={{ ...inputStyle, flex: 1, background: "rgba(0,0,0,0.04)", color: "#374151", fontFamily: "monospace", fontSize: "0.7rem", cursor: "text" }}
                />
                <button
                  data-testid="mcp-token-copy-btn"
                  onClick={() => handleCopy(mcpToken)}
                  title={copied ? "Copied!" : "Copy token"}
                  style={{
                    padding: "0.6rem 0.75rem", borderRadius: "0.5rem",
                    border: "1px solid rgba(26,77,46,0.2)",
                    background: copied ? "rgba(26,77,46,0.1)" : "rgba(255,255,255,0.6)",
                    cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0,
                    transition: "background 0.15s",
                  }}
                >
                  {copied ? <Check size={14} color="#3d6b1f" /> : <Copy size={14} color="#6b7280" />}
                </button>
              </div>
            ) : (
              <div style={{
                padding: "0.75rem", borderRadius: "0.5rem",
                background: "rgba(0,0,0,0.03)",
                border: "1px dashed rgba(26,77,46,0.2)",
                fontFamily: "'Manrope', sans-serif", color: "#9ca3af",
                fontSize: "0.82rem", textAlign: "center",
              }}>
                Click "Generate Token" to create your MCP access token
              </div>
            )}
          </div>

          {/* Endpoint row */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>MCP Endpoint URL</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input
                readOnly
                value={`${BACKEND_URL}/api/mcp/`}
                style={{ ...inputStyle, flex: 1, background: "rgba(0,0,0,0.04)", color: "#374151", fontFamily: "monospace", fontSize: "0.78rem", cursor: "text" }}
              />
              <button
                onClick={() => handleCopy(`${BACKEND_URL}/api/mcp/`)}
                title="Copy endpoint"
                style={{
                  padding: "0.6rem 0.75rem", borderRadius: "0.5rem",
                  border: "1px solid rgba(26,77,46,0.2)",
                  background: "rgba(255,255,255,0.6)", cursor: "pointer",
                  display: "flex", alignItems: "center", flexShrink: 0,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.6)")}
              >
                <Copy size={14} color="#6b7280" />
              </button>
            </div>
          </div>

          {/* Client config snippet */}
          {mcpToken && (
            <div style={{ marginBottom: "1rem" }}>
              <label style={labelStyle}>Cursor / VS Code Config</label>
              <div style={{ position: "relative" }}>
                <pre style={{
                  margin: 0, padding: "0.75rem", borderRadius: "0.5rem",
                  background: "rgba(15,23,42,0.06)", border: "1px solid rgba(26,77,46,0.15)",
                  fontFamily: "monospace", fontSize: "0.72rem", color: "#1e293b",
                  overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre",
                }}>{`{
  "mcpServers": {
    "serenity": {
      "url": "${BACKEND_URL}/api/mcp/",
      "headers": {
        "Authorization": "Bearer ${mcpToken}"
      }
    }
  }
}`}</pre>
                <button
                  data-testid="mcp-config-copy-btn"
                  onClick={() => handleCopy(`{\n  "mcpServers": {\n    "serenity": {\n      "url": "${BACKEND_URL}/api/mcp/",\n      "headers": {\n        "Authorization": "Bearer ${mcpToken}"\n      }\n    }\n  }\n}`)}
                  title="Copy config"
                  style={{
                    position: "absolute", top: "0.5rem", right: "0.5rem",
                    padding: "0.3rem 0.5rem", borderRadius: "0.35rem",
                    border: "1px solid rgba(26,77,46,0.2)",
                    background: "rgba(255,255,255,0.8)", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "0.3rem",
                    fontSize: "0.7rem", fontFamily: "'Manrope', sans-serif", color: "#6b7280",
                  }}
                >
                  <Copy size={11} /> Copy
                </button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
            <button
              data-testid="mcp-generate-token-btn"
              onClick={handleGenerateMcpToken}
              disabled={mcpLoading}
              style={{
                padding: "0.6rem 1.2rem", borderRadius: "0.5rem",
                border: "none",
                background: mcpLoading ? "rgba(61,107,31,0.4)" : "#3d6b1f",
                color: "#fff", fontSize: "0.875rem", fontWeight: 600,
                fontFamily: "'Manrope', sans-serif",
                cursor: mcpLoading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "0.4rem",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => { if (!mcpLoading) e.currentTarget.style.background = "#4a7f25"; }}
              onMouseLeave={(e) => { if (!mcpLoading) e.currentTarget.style.background = "#3d6b1f"; }}
            >
              <RefreshCw size={14} />
              {mcpLoading ? "Generating…" : mcpToken ? "Regenerate Token" : "Generate Token"}
            </button>
            {mcpToken && (
              <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.75rem" }}>
                Keep this token private — it grants full access to your account.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
